/** @jsxImportSource @emotion/react */
'use memo'

import css, { SerializedStyles } from '@emotion/css/macro'
import React, { useEffect } from 'react'
import { useField, useFormikContext } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import { formatFieldValue } from '@sportninja/common/features/profile/player'
import { useCountries } from '@sportninja/common/hooks/useCountries'
import { useProvinces } from '@sportninja/common/hooks/useProvinces'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import { DateOfBirthInput } from 'src/components/ts-components/DateOfBirthInput'
import ModernPhoneInput from 'src/components/ts-components/ModernPhoneInput'
import { CountryAndProvinceInput } from 'src/components/ts-components/CountryAndProvinceInput'
import { TextAreaInput } from 'src/components/ts-components/TextAreaInput'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import { ErrorLabel } from 'src/components/ts-components/ErrorLabel'
import { useFieldReadMode } from '../context/SectionEditContext'
import ReadOnlyField from './ReadOnlyField'

/** Build the (fieldErrors, customError) record pair the shared inputs expect. */
const useErrorProps = (name: string) => {
  const [, meta] = useField(name)
  const show = Boolean(meta.touched && meta.error)
  const fieldErrors = show ? { [name]: meta.error } : {}
  const customError = show ? { [name]: meta.error } : {}
  return { fieldErrors, customError }
}

/**
 * Returns a value setter that also clears the field's error on edit. The form
 * runs with validateOnChange off, so a server-set (or submit-set) error would
 * otherwise linger after the user fixes the field — this clears it the moment
 * they change the value. No-op clear when there is no error (avoids needless
 * Formik churn).
 */
const useClearErrorOnChange = (name: string) => {
  const [, meta, helpers] = useField(name)
  const { setFieldError } = useFormikContext()
  return (value: unknown) => {
    helpers.setValue(value)
    if (meta.error) {
      setFieldError(name, undefined)
    }
  }
}

interface BaseProps {
  name: string
  label: string
  disabled?: boolean
  placeholder?: string
  /** Shows a `*` after the label. Optional → existing callers are unaffected. */
  required?: boolean
}

/**
 * One grid cell wrapping a shared input. The shared inputs render their label,
 * control, and error as sibling fragments with no wrapper; placed directly in a
 * grid they'd each land in separate cells (label beside input). This stacks them
 * vertically — label ON TOP — and makes the cell the single grid item.
 */
const FieldCell: React.FC<{
  children: React.ReactNode
  /**
   * Marks a cell whose control has no antd error status (Switch), so
   * `scrollToFirstFieldError` still finds it.
   */
  hasError?: boolean
}> = ({ children, hasError }) => (
  <div css={fieldCellStyle} data-field-error={hasError ? 'true' : undefined}>
    {children}
  </div>
)

/** Text field bound to Formik. */
export const TextField: React.FC<
  BaseProps & {
    maxLength?: number
    helperText?: string
    /**
     * Colour of the helper text under the field. Defaults to the muted
     * `NEUTRAL_50` used for incidental hints; pass a token such as
     * `colors.PRIMARY_300` when the note carries a consequence the user should
     * notice -- "adding an email will send an invite", say.
     */
    helperTextColor?: string
    /** Label for assistive tech only — see TextInput's `hideLabel`. */
    hideLabel?: boolean
    /** TextInput's `size`: `large` is body size (16px). */
    size?: 'default' | 'large'
  }
> = ({
  name,
  label,
  disabled,
  placeholder,
  maxLength,
  required,
  helperText,
  helperTextColor = colors.NEUTRAL_50,
  hideLabel,
  size,
}) => {
  const [field] = useField(name)
  const setValue = useClearErrorOnChange(name)
  const { fieldErrors, customError } = useErrorProps(name)
  const isReadMode = useFieldReadMode()
  if (isReadMode) {
    return (
      <ReadOnlyField
        label={label}
        value={formatFieldValue('text', field.value)}
      />
    )
  }
  return (
    <FieldCell>
      <TextInput
        name={name}
        label={label}
        value={field.value ?? ''}
        setValue={(v) => setValue(v)}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        required={required}
        hideLabel={hideLabel}
        size={size}
      />
      {helperText ? (
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={helperTextColor}
        >
          {helperText}
        </Text>
      ) : null}
    </FieldCell>
  )
}

/** Multi-line text field bound to Formik. */
export const TextAreaField: React.FC<
  BaseProps & { maxLength?: number; rows?: number }
> = ({ name, label, disabled, placeholder, maxLength, rows = 3 }) => {
  const [field, meta] = useField(name)
  const setValue = useClearErrorOnChange(name)
  const { customError } = useErrorProps(name)
  // TextAreaInput reads fieldErrors as an array of erroring field names (it
  // calls fieldErrors.includes(name)), like DateOfBirthInput. The API message
  // still shows: ErrorLabel prefers customError over the array's fallback copy.
  const show = Boolean(meta.touched && meta.error)
  const fieldErrors = show ? [name] : []
  const isReadMode = useFieldReadMode()
  if (isReadMode) {
    return (
      <ReadOnlyField
        label={label}
        value={formatFieldValue('textarea', field.value)}
      />
    )
  }
  return (
    <FieldCell>
      <TextAreaInput
        name={name}
        label={label}
        value={field.value ?? ''}
        setValue={(v) => setValue(v)}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={rows}
      />
    </FieldCell>
  )
}

/**
 * Boolean switch bound to Formik. The switch follows the label on one line,
 * top-aligned so the label lines up with its neighbours' in the field grid; an
 * API error for the field shows underneath.
 */
export const SwitchField: React.FC<Omit<BaseProps, 'placeholder'>> = ({
  name,
  label,
  disabled,
}) => {
  const [field] = useField<boolean>(name)
  const setValue = useClearErrorOnChange(name)
  const { fieldErrors, customError } = useErrorProps(name)
  const isReadMode = useFieldReadMode()
  if (isReadMode) {
    return (
      <ReadOnlyField
        label={label}
        value={formatFieldValue('switch', field.value)}
      />
    )
  }
  return (
    <FieldCell hasError={Boolean(customError[name])}>
      <SwitchInput
        inline
        title={label}
        value={Boolean(field.value)}
        onChange={(v) => setValue(v)}
        disabled={disabled}
        dataTestId={`switch-${name}`}
      />
      <ErrorLabel
        name={name}
        fieldErrors={fieldErrors}
        customError={customError}
      />
    </FieldCell>
  )
}

/** Numeric text field (height/weight). Keeps value as a string in Formik. */
export const NumberField: React.FC<BaseProps & { addonAfter?: string }> = ({
  name,
  label,
  disabled,
  placeholder,
  addonAfter,
  required,
}) => {
  const [field] = useField(name)
  const setValue = useClearErrorOnChange(name)
  const { fieldErrors, customError } = useErrorProps(name)
  const isReadMode = useFieldReadMode()
  if (isReadMode) {
    return (
      <ReadOnlyField
        label={label}
        value={formatFieldValue('number', field.value, { unit: addonAfter })}
      />
    )
  }
  return (
    <FieldCell>
      <TextInput
        name={name}
        label={label}
        type='number'
        value={field.value ?? ''}
        setValue={(v) => setValue(v)}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={disabled}
        placeholder={placeholder}
        addonAfter={addonAfter}
        required={required}
      />
    </FieldCell>
  )
}

/** Select field bound to Formik. */
export const SelectField: React.FC<
  BaseProps & {
    options: Array<{ label: string; value: string }>
    /** Type-to-filter. Off by default so existing short selects are unchanged. */
    showSearch?: boolean
    /**
     * Intercepts the selection instead of writing straight to Formik. For a
     * field whose change has SIDE EFFECTS on other fields — the event form's
     * timezone rebases every date when it moves — so the handler can order its
     * own writes. It becomes responsible for setting this field's value too.
     */
    onChange?: (value: string) => void
    /**
     * Spinner in the dropdown while the options are still being fetched. For a
     * select whose list comes from an API (the event form's Team field) rather
     * than from a static constant.
     */
    loading?: boolean
  }
> = ({
  name,
  label,
  disabled = false,
  placeholder,
  options,
  required,
  showSearch = false,
  onChange,
  loading = false,
}) => {
  const [field] = useField(name)
  const setValue = useClearErrorOnChange(name)
  const { fieldErrors, customError } = useErrorProps(name)
  const isReadMode = useFieldReadMode()
  if (isReadMode) {
    return (
      <ReadOnlyField
        label={label}
        value={formatFieldValue('select', field.value, { options })}
      />
    )
  }
  // SelectInput has no `required` prop, but its label accepts a node — render
  // the asterisk inline to match TextInput's required marker.
  const labelNode = required ? (
    <>
      {label}
      <span css={requiredMarkStyle}>*</span>
    </>
  ) : (
    label
  )
  return (
    <FieldCell>
      <SelectInput
        name={name}
        label={labelNode}
        options={options}
        value={field.value ?? ''}
        onChange={(v) => (onChange ? onChange(v ?? '') : setValue(v ?? ''))}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={disabled}
        placeholder={placeholder}
        showSearch={showSearch}
        loading={loading}
        // Match on the visible label; antd's default also matches `value`,
        // which here is the same string, so this just makes it explicit.
        filterOption={
          showSearch
            ? (input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
            : undefined
        }
      />
    </FieldCell>
  )
}

/** Date-of-birth (YYYY-MM-DD) field bound to Formik. */
export const DateOfBirthField: React.FC<BaseProps> = ({
  name,
  label,
  disabled,
}) => {
  const [field, meta] = useField(name)
  const setValue = useClearErrorOnChange(name)
  const { customError } = useErrorProps(name)
  // DateOfBirthInput reads fieldErrors as an array of erroring field names
  // (it calls fieldErrors.includes(name)), unlike the other shared inputs which
  // read it as a record. Pass the array shape this input expects.
  const show = Boolean(meta.touched && meta.error)
  const fieldErrors = show ? [name] : []
  const isReadMode = useFieldReadMode()
  if (isReadMode) {
    return (
      <ReadOnlyField
        label={label}
        value={formatFieldValue('date', field.value)}
      />
    )
  }
  return (
    <FieldCell>
      <DateOfBirthInput
        name={name}
        label={label}
        value={field.value ?? ''}
        setValue={(v) => setValue(v)}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={disabled}
      />
    </FieldCell>
  )
}

/**
 * Phone field bound to Formik. Writes the E.164 value to `name` and the detected
 * country (ISO alpha-2) to `countryName` when provided.
 */
export const PhoneField: React.FC<BaseProps> = ({ name, label, disabled }) => {
  const [field, meta] = useField(name)
  const setValue = useClearErrorOnChange(name)
  // We store/send the full E.164 number, which already encodes the country, so
  // the country is not tracked separately. The input still shows the right flag
  // by deriving it from the value — onCountryChange is intentionally unused.
  const isReadMode = useFieldReadMode()
  if (isReadMode) {
    return (
      <ReadOnlyField
        label={label}
        value={formatFieldValue('phone', field.value)}
      />
    )
  }
  return (
    <FieldCell>
      <ModernPhoneInput
        label={label}
        value={field.value ?? ''}
        onChange={(v) => setValue(v)}
        disabled={disabled}
        error={meta.touched && meta.error ? meta.error : undefined}
      />
    </FieldCell>
  )
}

/**
 * A row of fields laid out as an N-column grid (Figma groups 2–3 fields per
 * row). Each child is a `FieldCell` (label stacked on top of its control), so
 * the columns align regardless of how many fields the row holds. Collapses to a
 * single column on narrow widths.
 */
export const FieldRow: React.FC<{
  cols?: 1 | 2 | 3
  /**
   * Explicit `grid-template-columns` for rows with unequal field widths (e.g.
   * Date of Birth, which holds three sub-selects, sharing a row with compact
   * height/weight inputs). Overrides `cols` when provided.
   */
  template?: string
  /**
   * Form-column width (px) below which the row collapses to a single column.
   * This is a CONTAINER width (the scrollable Content column), not the viewport
   * — so it reflects the real space available whether the app sidebar is open or
   * closed. Default 560. Rows holding compound inputs (DOB's 3 selects, the
   * nested height group) need a higher value so their sub-fields don't collide.
   */
  collapseAt?: number
  children: React.ReactNode
}> = ({ cols = 2, template, collapseAt = 560, children }) => (
  <div css={fieldRowStyle(cols, template, collapseAt)}>{children}</div>
)

/**
 * Country + Province pair bound to Formik. Wraps the shared
 * `CountryAndProvinceInput` in id-value mode so the numeric country/province ids
 * are written straight to Formik (`countryName`/`provinceName` fields). All
 * optional; province follows the selected country (the input clears province
 * when country changes). Used for the player's address.
 */
export const CountryProvinceField: React.FC<{
  countryName: string
  provinceName: string
  disabled?: boolean
  /**
   * Escape hatch for the pair's own container. `CountryAndProvinceInput`
   * carries a `margin-bottom: 8px` of its own, which stacks on top of a parent
   * form's row gap — a caller whose form already spaces its rows can zero it
   * out here rather than the shared input changing for every consumer.
   */
  containerStyle?: SerializedStyles
}> = ({ countryName, provinceName, disabled, containerStyle }) => {
  const [countryField, countryMeta] = useField(countryName)
  const [provinceField, provinceMeta] = useField(provinceName)
  const { setFieldValue, setFieldError } = useFormikContext()
  // Mirror the other fields' error gate (touched && error). The shared input
  // keys its error props by 'country'/'province', not the Formik field names.
  const showCountryError = Boolean(countryMeta.touched && countryMeta.error)
  const showProvinceError = Boolean(provinceMeta.touched && provinceMeta.error)
  const fieldErrors = {
    country: showCountryError ? countryMeta.error : undefined,
    province: showProvinceError ? provinceMeta.error : undefined,
  }
  const customErrors = {
    country: showCountryError ? countryMeta.error : undefined,
    province: showProvinceError ? provinceMeta.error : undefined,
  }
  // Seed the input's edit-mode initial ids ONCE at mount (it only needs them
  // before the country/province lists load). Binding the live Formik value here
  // would re-fire the input's id derivation on every keystroke anywhere in the
  // form.
  const initialCountryIdRef = React.useRef(countryField.value || null)
  const initialProvinceIdRef = React.useRef(provinceField.value || null)
  const isReadMode = useFieldReadMode()
  if (isReadMode) {
    return (
      <CountryProvinceReadOnly
        countryId={countryField.value}
        provinceId={provinceField.value}
      />
    )
  }
  return (
    <CountryAndProvinceInput
      valueMode='id'
      provinceValueMode='id'
      countryValue={countryField.value || ''}
      provinceValue={provinceField.value || ''}
      initialCountryId={initialCountryIdRef.current}
      initialProvinceId={initialProvinceIdRef.current}
      onCountryChange={(value) => {
        setFieldValue(countryName, value ?? '')
        if (countryMeta.error) {
          setFieldError(countryName, undefined)
        }
      }}
      onProvinceChange={(value) => {
        setFieldValue(provinceName, value ?? '')
        if (provinceMeta.error) {
          setFieldError(provinceName, undefined)
        }
      }}
      fieldErrors={fieldErrors}
      customErrors={customErrors}
      allowClear
      responsive
      disabled={disabled}
      containerStyle={containerStyle}
    />
  )
}

/**
 * Read mode of `CountryProvinceField`: the stored ids shown as names, in the
 * same Country | Province/State pair the input lays out.
 */
const CountryProvinceReadOnly: React.FC<{
  countryId: string | number | null | undefined
  provinceId: string | number | null | undefined
}> = ({ countryId, provinceId }) => {
  const { countries, refetch } = useCountries()
  const { provinces } = useProvinces(countryId || undefined)

  // useCountries loads on demand (from the store, else GET /countries).
  useEffect(() => {
    refetch()
  }, [refetch])

  const country = countries.find((c) => String(c.id) === String(countryId))
  const province = provinces.find((p) => String(p.id) === String(provinceId))

  return (
    <FieldRow cols={2}>
      <ReadOnlyField
        label='Country'
        value={formatFieldValue('text', country?.name)}
      />
      <ReadOnlyField
        label='Province/State'
        value={formatFieldValue('text', province?.name)}
      />
    </FieldRow>
  )
}

// ---- styled ----
const fieldRowStyle = (
  cols: 1 | 2 | 3,
  template: string | undefined,
  collapseAt: number
) => css`
  display: grid;
  grid-template-columns: ${template ?? `repeat(${cols}, 1fr)`};
  gap: 16px;
  align-items: start;
  /* Allow a nested row (e.g. the Height ft/in group) to shrink within its
     parent cell instead of overflowing it. */
  min-width: 0;

  /* Collapse on the FORM COLUMN's width, not the viewport's. The Content column
     (ScrollSpyLayout) is a container-type: inline-size container, so this tracks
     the real available width and stays correct as the sidebar opens/closes. */
  @container (max-width: ${collapseAt}px) {
    grid-template-columns: 1fr;
  }
`

const fieldCellStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const requiredMarkStyle = css`
  margin-left: 4px;
`
